import React from 'react';
import { OrderItem } from '../../types/dispense';

interface EditOrderModalProps {
  editingOrder: OrderItem | null;
  isAddingNewItem: boolean;
  setEditingOrder: (order: OrderItem | null) => void;
  setIsAddingNewItem: (value: boolean) => void;
  onSave: () => Promise<void>;
}

export function EditOrderModal({
  editingOrder, isAddingNewItem,
  setEditingOrder, setIsAddingNewItem, onSave
}: EditOrderModalProps) {
  if (!editingOrder) return null;

  const handleClose = () => {
    setEditingOrder(null);
    setIsAddingNewItem(false);
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{isAddingNewItem ? 'Add Order Item' : 'Edit Order Item'}</div>
          <button className="modal-close" onClick={handleClose}>&times;</button>
        </div>

        <div className="edit-order-form">
          <div className="form-group">
            <label>Item Name</label>
            <input type="text" value={editingOrder.publishName || editingOrder.name} disabled />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Quantity</label>
              <div className="input-with-unit">
                <input
                  type="number"
                  value={editingOrder.quantity || editingOrder.qty || 1}
                  onChange={(e) => setEditingOrder({
                    ...editingOrder,
                    quantity: parseFloat(e.target.value) || 1,
                    qty: parseFloat(e.target.value) || 1
                  })}
                  min="0.1"
                  step="0.1"
                />
                <span className="input-unit">{editingOrder.dispenseUOM || 'unit'}</span>
              </div>
            </div>
            <div className="form-group">
              <label>Unit Price</label>
              <div className="input-with-unit">
                <span className="input-unit">$</span>
                <input
                  type="number"
                  value={editingOrder.unitPrice || editingOrder.price}
                  onChange={(e) => setEditingOrder({
                    ...editingOrder,
                    unitPrice: parseFloat(e.target.value) || 0,
                    price: parseFloat(e.target.value) || 0
                  })}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {editingOrder.type === 'MED' && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Dosage (Tablets/Dose)</label>
                  <div className="input-with-unit">
                    <input
                      type="number"
                      value={editingOrder.dosage || ''}
                      onChange={(e) => setEditingOrder({
                        ...editingOrder,
                        dosage: parseFloat(e.target.value) || undefined
                      })}
                      min="0"
                      step="0.01"
                      placeholder="e.g., 1-2"
                    />
                    <span className="input-unit">{editingOrder.doseUOM || 'tablet'}</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Dose UOM</label>
                  <select
                    value={editingOrder.doseUOM || 'tablet'}
                    onChange={(e) => setEditingOrder({ ...editingOrder, doseUOM: e.target.value })}
                  >
                    <option value="tablet">Tablet</option>
                    <option value="capsule">Capsule</option>
                    <option value="ml">ml</option>
                    <option value="iu">iu</option>
                    <option value="mg">mg</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Frequency</label>
                <input
                  type="text"
                  value={editingOrder.frequency || ''}
                  onChange={(e) => setEditingOrder({ ...editingOrder, frequency: e.target.value })}
                  placeholder="e.g., TDS, BD, OD, ON"
                />
              </div>

              <div className="form-group">
                <label>Instruction</label>
                <input
                  type="text"
                  value={editingOrder.instruction || ''}
                  onChange={(e) => setEditingOrder({ ...editingOrder, instruction: e.target.value })}
                  placeholder="e.g., After food, Before food"
                />
              </div>

              <div className="form-group">
                <label>Purpose</label>
                <input
                  type="text"
                  value={editingOrder.purpose || ''}
                  onChange={(e) => setEditingOrder({ ...editingOrder, purpose: e.target.value })}
                  placeholder="Purpose of medication"
                />
              </div>

              <div className="form-group">
                <label>Precaution</label>
                <input
                  type="text"
                  value={editingOrder.precaution || ''}
                  onChange={(e) => setEditingOrder({ ...editingOrder, precaution: e.target.value })}
                  placeholder="Precautions or warnings"
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label>Dispense UOM</label>
            <select
              value={editingOrder.dispenseUOM || 'unit'}
              onChange={(e) => setEditingOrder({ ...editingOrder, dispenseUOM: e.target.value })}
            >
              <option value="tablet">Tablet</option>
              <option value="capsule">Capsule</option>
              <option value="bottle">Bottle</option>
              <option value="box">Box</option>
              <option value="unit">Unit</option>
              <option value="ml">ml</option>
            </select>
          </div>

          {/* Summary Display - Matching CheckLine's FormDispense format */}
          <div className="order-summary-preview" style={{ marginTop: 16, padding: 16, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e0e0e0' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: 12, color: '#333' }}>Order Summary</div>
            <div style={{ fontSize: '13px', lineHeight: '1.8', color: '#555' }}>
              <div><strong>Quantity:</strong> {editingOrder.quantity || editingOrder.qty || 1} {editingOrder.dispenseUOM || 'unit'}</div>
              <div><strong>Price:</strong> ${(editingOrder.unitPrice || editingOrder.price || 0).toFixed(2)}</div>
              {editingOrder.type === 'MED' && editingOrder.dosage && (
                <div><strong>Dosage:</strong> {editingOrder.dosage} {editingOrder.doseUOM || 'tablet'}</div>
              )}
              {editingOrder.frequency && (
                <div><strong>Frequency:</strong> {editingOrder.frequency}</div>
              )}
              {editingOrder.instruction && (
                <div><strong>Instruction:</strong> {editingOrder.instruction}</div>
              )}
              {editingOrder.precaution && (
                <div><strong>Precaution:</strong> {editingOrder.precaution}</div>
              )}
              {editingOrder.purpose && (
                <div><strong>Purpose:</strong> {editingOrder.purpose}</div>
              )}
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #ddd' }}>
                <strong>Subtotal:</strong> ${((editingOrder.unitPrice || editingOrder.price) * (editingOrder.quantity || editingOrder.qty || 1)).toFixed(2)}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={handleClose}>Cancel</button>
          <button className="btn btn-primary" onClick={onSave}>
            {isAddingNewItem ? 'Add Item' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
